/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useGetActiveSubscriptionsQuery } from 'src/api_services/subscriptions/query';
import { Box } from 'src/components/Box';
import { Card, CardBody, CardFooter } from 'src/components/Card';
import { Modal } from 'src/components/Modal';
import { SelectField } from 'src/components/Select';
import { H3, Text } from 'src/components/Text';
import {
  GithubRepositoryInfo,
  PersistentAppKind,
  PersistentAppStartMode,
  WebsiteGithubConnectionRefKindEnum,
  WebsiteKind,
  WebsiteStatus,
} from 'src/orchd-client';
import { sessionSelectors } from 'src/store/session/selectors';
import { NewWebsiteFields } from './NewWebsiteFields';
import { Form } from 'src/containers/Form';
import { FormType } from 'src/containers/Form';
import * as Yup from 'yup';
import { Loading } from 'src/components/Loading';
import { websitesApi } from 'src/api_services/websites';
import { Button } from 'src/components/Button';
import { messages } from 'src/messages';
import { StepTwo } from './StepTwo';
import { FormikHelpers } from 'formik';
import { useHistory } from 'react-router-dom';
import { useToast } from 'src/utils/toast/useToast';
import { AddPersistentAppSchema } from '../websites/website/dashboard/NodeJs/CreateNodeAppForm';
import { appsApi } from 'src/api_services/apps';
import { domainRegex, noHttpOrWww, subDomainRegex } from 'src/utils/regex';
import { ServerPlacementSection } from './ServerPlacementSection';
import { useGetWebsitesQuery } from 'src/api_services/websites/query';
import { useGetWebsiteAppsQuery } from 'src/api_services/apps/query';
import { SelectField as NewSelectField } from 'src/components/SelectNew';
import sortBy from 'lodash/sortBy';
import { Icon } from 'src/components/Icon';
import { KindPillMap } from '../websites/dashboard/Domains/KindPill';
import { getPillStatus, isWebsiteActive } from 'src/utils/website';
import { MiddleTruncate } from 'src/components/MiddleTruncate';
import { StatusPill } from 'src/components/StatusPill';
import {
  normalisePublishDirectory,
  publishDirectoryErrorMessage,
  validatePublishDirectory,
} from 'src/utils/publishDirectory';

import {
  AddWebsiteGithubProgress,
  useGithubDeploySteps,
} from 'src/pages/websites/add-website/AddWebsite/AddWebsiteGithubProgress';

export enum GithubDeployTargetKind {
  newWebsite = 'newWebsite',
  existingWebsite = 'existingWebsite',
}

export interface GithubWebsiteDeployFormShape {
  autoDeploy: boolean;
  buildCommand?: string;
  publishDirectory?: string;
  refKind: WebsiteGithubConnectionRefKindEnum;
  branchOrTagName: string;
  existingWebsiteId?: string;
  newWebsite?: {
    domain: string;
    subscriptionId?: number;
    kind: WebsiteKind.normal | WebsiteKind.staging;
    serverGroupId?: string;
    appServerId?: string;
    dbServerId?: string;
    postgresqlServerId?: string;
    backupServerId?: string;
    emailServerId?: string;
  };
  persistentApp?: AddPersistentAppSchema;
}

export const DeployToWebsite = ({
  name,
  full_name,
  owner,
  targetKind = GithubDeployTargetKind.newWebsite,
}: GithubRepositoryInfo & { targetKind?: GithubDeployTargetKind }) => {
  const history = useHistory();

  const intl = useIntl();

  const toast = useToast();
  const {
    steps: githubSteps,
    initSteps,
    runStep,
    triggerAndFollowDeploy,
    deployLog: githubDeployLog,
  } = useGithubDeploySteps();

  // shown while a deploy submit is in flight; rendered outside the
  // modal tree (as a Modal sibling) so the overlay anchors to the
  // page the same way AddWebsite's own submit animation does
  const [githubSubmitting, setGithubSubmitting] = useState(false);

  const { orgId, isMasterOrg } = useSelector(sessionSelectors.getMember);

  const { data: subscriptions, isLoading: subscriptionsLoading } = useGetActiveSubscriptionsQuery({
    orgId,
    enabled: !isMasterOrg,
  });

  const { data: websitesData, isLoading: websitesLoading } = useGetWebsitesQuery({
    orgId,
    sortBy: 'domain',
    sortOrder: 'asc',
    showDeleted: false,
    enabled: targetKind === GithubDeployTargetKind.existingWebsite,
  });

  const websites = useMemo(() => {
    if (!websitesData) return [];
    return sortBy(
      websitesData.items
        .filter((website) => [WebsiteKind.normal, WebsiteKind.staging].includes(website.kind))
        .filter((website) => website.status === WebsiteStatus.active && !website.suspendedBy),
      (w) => w.domain.domain
    );
  }, [websitesData]);

  const [appsCheckWebsiteId, setAppsCheckWebsiteId] = useState<string>();

  const { data: appsCheckApps, isLoading: appsCheckLoading } = useGetWebsiteAppsQuery({
    orgId,
    websiteId: appsCheckWebsiteId ?? '',
    enabled: targetKind === GithubDeployTargetKind.existingWebsite && !!appsCheckWebsiteId,
  });

  const selectedWebsiteHasApps = !!appsCheckWebsiteId && (appsCheckApps?.length ?? 0) > 0;

  const [step, setStep] = useState<1 | 2>(1);

  const [placementOpen, setPlacementOpen] = useState(false);

  const formRef = React.useRef<FormType<GithubWebsiteDeployFormShape> | null>(null);

  // When the installed-apps check for the selected existing website settles,
  // give the field the look of a blur (touched) and re-run schema validation so
  // the noInstalledApps error appears/clears without waiting for further input.
  useEffect(() => {
    if (targetKind !== GithubDeployTargetKind.existingWebsite || !appsCheckWebsiteId || appsCheckLoading) {
      return;
    }

    const form = formRef.current;

    if (!form) {
      return;
    }

    if (selectedWebsiteHasApps) {
      form.setFieldTouched('existingWebsiteId', true);
    }

    // re-validate with the settled noInstalledApps test
    form.setFieldValue('existingWebsiteId', form.values.existingWebsiteId);
  }, [targetKind, appsCheckWebsiteId, appsCheckLoading, selectedWebsiteHasApps]);

  // Listen for Enter key to advance to step 2
  useEffect(() => {
    // Enter inside a multiline field (textarea / contenteditable) must insert
    // a newline, not advance the step or submit the form.
    const isMultilineInput = (target: EventTarget | null): boolean =>
      target instanceof HTMLElement && (target.tagName === 'TEXTAREA' || target.isContentEditable);

    if (step === 1) {
      const handleKeyDown = async (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !isMultilineInput(e.target)) {
          // Trigger validation for the domain field via Formik's touched handling
          formRef.current?.setFieldTouched('newWebsite.domain', true, true);
          formRef.current?.setFieldValue('newWebsite.domain', formRef.current?.values?.newWebsite?.domain, true);
          const domainError = formRef.current?.errors?.newWebsite?.domain;
          if (domainError && targetKind === GithubDeployTargetKind.newWebsite) {
            return;
          }

          if (
            targetKind === GithubDeployTargetKind.existingWebsite &&
            (!formRef.current?.values?.existingWebsiteId || appsCheckLoading || selectedWebsiteHasApps)
          ) {
            return;
          }
          setStep(2);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    if (step === 2) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !isMultilineInput(e.target) && !formRef.current?.isSubmitting) {
          formRef.current?.submitForm();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [step, formRef, targetKind, appsCheckLoading, selectedWebsiteHasApps]);

  const defaultSubscriptionId = useMemo(
    () => (!isMasterOrg && subscriptions?.length ? subscriptions[0].id : undefined),
    [isMasterOrg, subscriptions]
  );

  useEffect(() => {
    if (formRef.current && !formRef.current.values?.newWebsite?.subscriptionId && defaultSubscriptionId !== undefined) {
      formRef.current.setFieldValue('newWebsite.subscriptionId', defaultSubscriptionId);
    }
  }, [defaultSubscriptionId, formRef]);

  const getPersistentAppsAllowed = (values: GithubWebsiteDeployFormShape) => {
    if (isMasterOrg) {
      return true;
    }
    const selectedSubscription =
      targetKind === GithubDeployTargetKind.existingWebsite
        ? subscriptions?.find(
            (sub) => sub.id === websites.find((w) => w.id === values.existingWebsiteId)?.subscriptionId
          )
        : subscriptions?.find((sub) => sub.id === values.newWebsite?.subscriptionId);
    return selectedSubscription?.persistentAppsAllowed ?? false;
  };

  const repoOption = useMemo(
    () => ({
      label: (
        <Box a="center" wrap="nowrap">
          {owner?.avatar_url && (
            <img
              src={owner?.avatar_url}
              alt=""
              width={16}
              height={24}
              loading="lazy"
              style={{
                marginRight: '8px',
                width: 16,
                height: 16,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          )}
          <Text color="dark" ellipsis>
            {full_name}
          </Text>
        </Box>
      ),
      value: 'x',
    }),
    [full_name, owner]
  );

  if (subscriptionsLoading && !isMasterOrg) {
    return (
      <Modal width={1000}>
        <Loading />
      </Modal>
    );
  }

  const schema = Yup.object({
    autoDeploy: Yup.boolean(),
    buildCommand: Yup.string(),
    publishDirectory: Yup.string()
      .max(255)
      .test('publishDirectoryRelative', publishDirectoryErrorMessage, (value) => validatePublishDirectory(value)),
    branchOrTagName: Yup.string().required(),
    refKind: Yup.mixed<WebsiteGithubConnectionRefKindEnum>()
      .oneOf(Object.values(WebsiteGithubConnectionRefKindEnum))
      .required(),
    existingWebsiteId:
      targetKind === GithubDeployTargetKind.existingWebsite
        ? Yup.string()
            .required('Please select a website')
            .test(
              'noInstalledApps',
              intl.formatMessage({
                id: 'git_deploy.apps_installed_error',
                defaultMessage: 'Cannot configure GitHub on a website which already has apps installed',
              }),
              (value) => !(value && appsCheckWebsiteId === value && selectedWebsiteHasApps)
            )
        : Yup.string(),
    newWebsite:
      targetKind === GithubDeployTargetKind.newWebsite
        ? Yup.object({
            domain: Yup.string()
              .when(['kind'], {
                is: (kind: WebsiteKind) => kind === WebsiteKind.staging,
                then: Yup.string()
                  .matches(subDomainRegex, {
                    message: intl.formatMessage(messages['sub_domain_name.invalid']),
                  })
                  .required(intl.formatMessage(messages['sub_domain_name.required'])),
              })
              .when(['kind'], {
                is: (kind: WebsiteKind) => kind !== WebsiteKind.staging,
                then: Yup.string()
                  .matches(domainRegex, { message: intl.formatMessage(messages['domain_name.invalid']) })
                  .matches(noHttpOrWww, {
                    message: 'Please enter a domain name without www.',
                  })
                  .required(intl.formatMessage(messages['domain_name.required'])),
              }),
            subscriptionId: Yup.number()
              .when('isMasterOrg', {
                is: false,
                then: (schema) => schema.required('Subscription is required'),
              })
              .optional(),
            kind: Yup.mixed<WebsiteKind>().oneOf(Object.values(WebsiteKind)).optional(),
          }).required()
        : Yup.object().optional(),
  });

  const initialValues: GithubWebsiteDeployFormShape = {
    autoDeploy: false,
    refKind: WebsiteGithubConnectionRefKindEnum.branch,
    branchOrTagName: '',
    existingWebsiteId: undefined,
    newWebsite: {
      domain: '',
      kind: WebsiteKind.normal,
      subscriptionId: defaultSubscriptionId,
    },
    persistentApp: {
      appKind: undefined,
      command: '',
      version: '',
      startMode: PersistentAppStartMode.automatic,
    },
  };

  const runGithubSubmit = async (
    values: GithubWebsiteDeployFormShape,
    form: FormikHelpers<GithubWebsiteDeployFormShape>,
    onSuccess: () => void
  ) => {
    let websiteId: string;

    initSteps({
      withCreateWebsite: !(targetKind === GithubDeployTargetKind.existingWebsite && values.existingWebsiteId),
      domain: values.newWebsite?.domain,
      repoFullName: full_name,
      withApp: getPersistentAppsAllowed(values) && !!values.persistentApp?.appKind,
      withAutoDeploy: values.autoDeploy,
    });

    if (targetKind === GithubDeployTargetKind.existingWebsite && values.existingWebsiteId) {
      websiteId = values.existingWebsiteId;
    } else {
      const result = await runStep('createWebsite', () =>
        websitesApi.createWebsite({
          newWebsite: values.newWebsite,
          orgId,
        })
      );
      websiteId = result.data.id;
    }

    await runStep('connectRepo', () =>
      websitesApi.connectWebsiteGit({
        websiteId,
        orgId,
        websiteGithubConnection: {
          branchOrTagName: values.branchOrTagName,
          refKind: values.refKind,
          repoOwner: owner.login,
          repoName: name,
          autoDeploy: values.autoDeploy,
          buildCommand: values.buildCommand,
          publishDirectory: normalisePublishDirectory(values.publishDirectory),
        },
      })
    );

    if (getPersistentAppsAllowed(values) && values.persistentApp?.appKind) {
      const { version, ...persistentApp } = values.persistentApp;
      await runStep('installApp', () =>
        appsApi.createWebsitePersistentApp({
          websiteId,
          persistentApp: {
            ...persistentApp,
            workingDirectory: 'deploys/current',
            ...(values.persistentApp.appKind === PersistentAppKind.nodejs && { nodeVersion: version }),
          },
        })
      );
    }

    // the initial deploy only runs when the user selected auto deployment
    const deployed = values.autoDeploy ? await triggerAndFollowDeploy({ orgId, websiteId }) : true;

    onSuccess();

    if (deployed) {
      toast.success(
        targetKind === GithubDeployTargetKind.existingWebsite ? (
          <FormattedMessage
            id="git_deploy.github_connected_toast"
            defaultMessage="GitHub connected to {domain}"
            values={{ domain: websites.find((website) => website.id === websiteId)?.domain.domain ?? '' }}
          />
        ) : (
          <FormattedMessage
            id="git_deploy.website_action_toast"
            defaultMessage="{target, select, newWebsite {Website created} existingWebsite {Git connected} other {Done}}"
            values={{ target: targetKind }}
          />
        )
      );
    }

    history.push(`/websites/${websiteId}/git#deployments`);
  };

  const handleSubmit = async (
    values: GithubWebsiteDeployFormShape,
    form: FormikHelpers<GithubWebsiteDeployFormShape>,
    onSuccess: () => void
  ) => {
    setGithubSubmitting(true);

    try {
      await runGithubSubmit(values, form, onSuccess);
    } finally {
      setGithubSubmitting(false);
    }
  };

  return (
    <>
      {githubSubmitting && <AddWebsiteGithubProgress steps={githubSteps} deployLog={githubDeployLog} />}
      <Modal width={1000}>
        {({ hide }) => {
          return (
            <Form
              initialValues={initialValues}
              validationSchema={schema}
              onSubmit={(values, form) => handleSubmit(values, form, hide)}
            >
              {(form) => {
                formRef.current = form;
                return (
                  <>
                    <Card marginBottom="lg">
                      <CardBody
                        css={{
                          maxHeight: 'calc(var(--modal-vh, 100vh) - 96px)',
                          overflowY: 'auto',
                        }}
                      >
                        <Box j="center" pt="lg" mb="2xl">
                          <H3>
                            <FormattedMessage
                              id="git_deploy.deploy_to_website"
                              defaultMessage="Deploy repository to {target}"
                              values={{
                                target:
                                  targetKind === GithubDeployTargetKind.newWebsite ? 'new website' : 'existing website',
                              }}
                            />
                          </H3>
                        </Box>
                        <Box px={{ xs: '0', md: 'lg' }} py="lg">
                          <Text size="sm" transform="uppercase" weight="bold">
                            <FormattedMessage
                              id="git_deploy.deploy_steps"
                              defaultMessage="Step {currentStep} of {totalSteps}"
                              values={{
                                currentStep: step,
                                totalSteps: 2,
                              }}
                            />
                          </Text>
                        </Box>

                        <Box px={{ xs: '0', md: 'lg' }}>
                          {step === 1 ? (
                            <>
                              <Box pb="lg" d="row" wrap={{ xs: 'wrap', md: 'nowrap' }}>
                                <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
                                  <SelectField
                                    options={[repoOption]}
                                    value={[repoOption]}
                                    name="repoDummy"
                                    label={<FormattedMessage id="git_deploy.repository" defaultMessage="Repository" />}
                                    onChange={() => {}}
                                    disabled
                                  />
                                </Box>

                                <Box w="auto" grow={1} a="center" p="lg" hide={{ xs: true, md: false }}>
                                  <Icon name="right" marginTop="xl" />
                                </Box>

                                {targetKind === GithubDeployTargetKind.newWebsite && (
                                  <Box w={{ xs: 12, md: 6 }} d="column" wrap="nowrap">
                                    <NewWebsiteFields form={form} subscriptions={subscriptions} />
                                  </Box>
                                )}

                                {targetKind === GithubDeployTargetKind.existingWebsite && (
                                  <Box w={{ xs: 12, md: 6 }} d="column" wrap="nowrap">
                                    <Box>
                                      <NewSelectField
                                        name="existingWebsiteId"
                                        isLoading={websitesLoading}
                                        label={<FormattedMessage {...messages.website} />}
                                        placeholder={
                                          <FormattedMessage
                                            id="git_deploy.select_a_website_placeholder"
                                            defaultMessage="Select a website"
                                          />
                                        }
                                        value={
                                          websites.find((w) => w.id === form.values.existingWebsiteId)
                                            ? [
                                                {
                                                  value: form.values.existingWebsiteId!,
                                                  label: websites.find((w) => w.id === form.values.existingWebsiteId)!
                                                    .domain.domain,
                                                },
                                              ]
                                            : []
                                        }
                                        options={websites.map((w) => ({
                                          value: w.id,
                                          searchableBy: w.domain.domain,
                                          label: (
                                            <MiddleTruncate
                                              delayDuration={0}
                                              prefix={
                                                <KindPillMap
                                                  showTooltip
                                                  domain={w.domain.domain}
                                                  kind={w.kind}
                                                  disabled={!isWebsiteActive(w)}
                                                />
                                              }
                                              suffix={<StatusPill ml="sm" status={getPillStatus(w)} />}
                                              text={w.domain.domain}
                                              size="md"
                                            />
                                          ),
                                        }))}
                                        renderValue={({ value: selected, open, placeholder }) => {
                                          // BaseValue behaves this way while a searchable dropdown is open
                                          if (open) {
                                            return null;
                                          }

                                          if (selected.length === 0) {
                                            return (
                                              <Text size="md" ellipsis color="grey">
                                                {placeholder}
                                              </Text>
                                            );
                                          }

                                          const selectedWebsite = websites.find((w) => w.id === selected[0].value);

                                          return (
                                            <Box d="row" a="center" wrap="nowrap">
                                              {selectedWebsite && (
                                                <KindPillMap
                                                  showTooltip
                                                  domain={selectedWebsite.domain.domain}
                                                  kind={selectedWebsite.kind}
                                                  disabled={!isWebsiteActive(selectedWebsite)}
                                                />
                                              )}
                                              <Text size="md" color="dark" ellipsis>
                                                {selected[0].label ?? placeholder}
                                              </Text>
                                            </Box>
                                          );
                                        }}
                                        onChange={({ value }) => {
                                          const selectedId = value[0]?.value as string | undefined;
                                          form.setFieldValue('existingWebsiteId', selectedId);
                                          setAppsCheckWebsiteId(selectedId);
                                        }}
                                        searchable
                                        requiredIndicator
                                        clearable={false}
                                        err={form.errors.existingWebsiteId}
                                        touched={form.touched.existingWebsiteId}
                                      />
                                    </Box>
                                  </Box>
                                )}
                              </Box>

                              {targetKind === GithubDeployTargetKind.newWebsite && (
                                <ServerPlacementSection
                                  form={form}
                                  isMasterOrg={isMasterOrg}
                                  subscriptions={subscriptions}
                                  placementOpen={placementOpen}
                                  onToggle={() => setPlacementOpen(!placementOpen)}
                                />
                              )}
                            </>
                          ) : (
                            (() => {
                              const persistentAppsAllowed = getPersistentAppsAllowed(form.values);
                              return (
                                <StepTwo
                                  form={form}
                                  repoOption={repoOption}
                                  orgId={orgId}
                                  full_name={full_name}
                                  persistentAppsAllowed={persistentAppsAllowed}
                                />
                              );
                            })()
                          )}
                        </Box>
                      </CardBody>
                      <CardFooter>
                        <Box d="row" j="flex-end" px="lg" a="center">
                          <Button variant="tertiary" size="small" onClick={() => hide()} marginRight="2xl">
                            <FormattedMessage {...messages.cancel} />
                          </Button>
                          {step === 1 ? (
                            <div tabIndex={0}>
                              <Button
                                variant="primary"
                                size="small"
                                onClick={() => setStep(2)}
                                disabled={
                                  (targetKind === GithubDeployTargetKind.newWebsite &&
                                    (!!form.errors?.newWebsite?.domain || !form.values.newWebsite.domain)) ||
                                  (targetKind === GithubDeployTargetKind.existingWebsite &&
                                    (!!form.errors?.existingWebsiteId ||
                                      !form.values.existingWebsiteId ||
                                      appsCheckLoading))
                                }
                              >
                                <FormattedMessage {...messages.next} />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="primary"
                              size="small"
                              disabled={!form.isValid}
                              onClick={() => form.submitForm()}
                              loading={form.isSubmitting}
                            >
                              <FormattedMessage
                                id="git_deploy.deploy_button"
                                defaultMessage="{target, select, newWebsite {Deploy} existingWebsite {Connect} other {Deploy}}"
                                values={{ target: targetKind }}
                              />
                            </Button>
                          )}
                        </Box>
                      </CardFooter>
                    </Card>
                  </>
                );
              }}
            </Form>
          );
        }}
      </Modal>
    </>
  );
};
